import { Article, ArticlePage, ArticleQuery, Feed } from "./types";

/**
 * Override with REACT_APP_API_BASE at build time to point the reader at a
 * local backend (e.g. http://localhost:3661/api).
 */
export const API_BASE =
  process.env.REACT_APP_API_BASE || "https://tezlens.purplematter.com/api";

export class ApiError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = "ApiError";
  }
}

function buildArticlesUrl(query: ArticleQuery): string {
  const params = new URLSearchParams();
  if (query.limit != null) params.set("limit", String(query.limit));
  if (query.offset) params.set("offset", String(query.offset));
  if (query.type) params.set("type", query.type);
  if (query.q) params.set("q", query.q);
  if (query.feeds && query.feeds.length > 0) {
    params.set("feeds", query.feeds.join(","));
  }
  return `${API_BASE}/articles?${params.toString()}`;
}

async function request(url: string, signal?: AbortSignal): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(url, { signal, headers: { Accept: "application/json" } });
  } catch (error) {
    if ((error as Error).name === "AbortError") throw error;
    throw new ApiError(
      "Could not reach the TezLens API. Check your connection and try again."
    );
  }
  if (!response.ok) {
    throw new ApiError(
      `The TezLens API responded with ${response.status}.`,
      response.status
    );
  }
  return response;
}

export async function fetchFeeds(signal?: AbortSignal): Promise<Feed[]> {
  const response = await request(`${API_BASE}/feeds`, signal);
  return (await response.json()) as Feed[];
}

export async function fetchArticles(
  query: ArticleQuery,
  signal?: AbortSignal
): Promise<ArticlePage> {
  const response = await request(buildArticlesUrl(query), signal);
  const items = (await response.json()) as Article[];

  // Total/paging live in headers so the body stays a plain array, which keeps
  // the endpoint usable by other consumers (e.g. the Bluesky bot).
  const total = Number(response.headers.get("X-Total-Count"));
  return {
    items,
    total: Number.isFinite(total) && total > 0 ? total : items.length,
    hasMore: response.headers.get("X-Has-More") === "true",
    generatedAt: response.headers.get("X-Generated-At"),
  };
}
